import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { rectFromPoints, roundTo } from '../types/geometry.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Face count check.
 *
 * `requires: ['faces']` and the detector is asked for up to two faces, so
 * "no face" and "more than one face" are both real, reportable outcomes rather
 * than silent passes. When the detector itself is unavailable the check returns
 * `not_checked` — it never guesses.
 */
export const faceCountCheck: ValidationCheck = {
  id: 'face_count',
  title: 'Одно лицо',
  description: 'Ищет лица и проверяет, что в кадре ровно одно.',
  kind: 'content',
  requires: ['faces'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['faces']);
    if (missing) return notChecked(ctx, faceCountCheck, missing, ctx.rule.severity);

    const min = numberParam(ctx.rule.params, 'min', 1);
    const max = numberParam(ctx.rule.params, 'max', 1);
    const faces = ctx.faces.faces;
    const count = faces.length;

    const geometry: NonNullable<CheckResult['geometry']> = [];
    const problemRegions: NonNullable<CheckResult['problemRegions']> = [];
    for (const face of faces) {
      const box =
        face.box ??
        rectFromPoints(
          face.landmarks.map((landmark) => ({ x: landmark.x, y: landmark.y })),
        );
      geometry.push({
        type: 'rect',
        coordinates: [
          roundTo(box.x, 4),
          roundTo(box.y, 4),
          roundTo(box.width, 4),
          roundTo(box.height, 4),
        ],
      });
      if (face.index > 0 && count > max) {
        problemRegions.push({
          id: `extra_face_${face.index}`,
          rect: {
            x: roundTo(box.x, 4),
            y: roundTo(box.y, 4),
            width: roundTo(box.width, 4),
            height: roundTo(box.height, 4),
          },
          message: 'Второе лицо в кадре.',
          severity: 'error',
        });
      }
    }

    const status = count >= min && count <= max ? 'pass' : 'fail';

    return plainResult({
      id: faceCountCheck.id,
      title: faceCountCheck.title,
      status,
      severity: ctx.rule.severity,
      value: count,
      min,
      max,
      unit: 'faces',
      message:
        status === 'pass'
          ? 'В кадре обнаружено одно лицо.'
          : count === 0
            ? 'Лицо не обнаружено — фотография на документ должна содержать лицо крупно и анфас.'
            : `В кадре обнаружено лиц (${count}) больше допустимого.`,
      requirement:
        min === max ? 'В кадре должно быть ровно одно лицо.' : `Лиц от ${min} до ${max}.`,
      fixability: count === 0 ? 'retake' : 'fixable',
      geometry,
      problemRegions,
      confidence: roundTo(ctx.faces.faces[0]?.confidence ?? 0.8, 2),
      details: { count, detector: ctx.faces.source, displayValue: `${count}` },
    });
  },
};
